import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  getHotelSearchFormSchema,
  type HotelSearchFormInputValues,
  type HotelSearchFormValues,
} from "@/features/hotels/utils/hotelsValidation";
import { usePreviousHotelSearchData } from "@/features/search/hooks/usePreviousSearchData";
import { useSnackbarRHFErrors } from "@/hooks/useSnackbarRHFErrors";
import type { SearchSource } from "@/lib/rudderstack";

import { HotelDatePicker } from "./sections/HotelDatePicker";
import { HotelDestinationPicker } from "./sections/HotelDestinationPicker";
import { HotelVisitorsPicker } from "./sections/HotelVisitorsPicker";

export type HotelSearchForm = UseFormReturn<
  HotelSearchFormInputValues,
  unknown,
  HotelSearchFormValues
>;

type HotelSearchFormProps = {
  variant?: "default" | "compact";
  submitButton?: React.ReactNode;
  initialValues?: HotelSearchFormInputValues | null;
  showErrorsInline?: boolean;
  inputsWrappedInCard?: boolean;
  analyticsSource: SearchSource;
  onSubmit?: (values: HotelSearchFormValues) => void;
  onChange?: (values: HotelSearchFormInputValues) => void;
};

export function HotelSearchForm({
  variant = "default",
  onSubmit,
  onChange,
  initialValues,
  showErrorsInline,
  inputsWrappedInCard,
  submitButton,
  analyticsSource,
}: HotelSearchFormProps) {
  const intl = useIntl();

  const { previousSearchData } = usePreviousHotelSearchData();

  const schema = useMemo(() => getHotelSearchFormSchema(intl), [intl]);

  const form = useForm<
    HotelSearchFormInputValues,
    unknown,
    HotelSearchFormValues
  >({
    defaultValues: initialValues || previousSearchData,
    mode: showErrorsInline ? "onBlur" : "onSubmit",
    reValidateMode: showErrorsInline ? "onChange" : "onSubmit",
    resolver: zodResolver(schema),
  });

  useSnackbarRHFErrors<HotelSearchFormInputValues>({
    disabled: showErrorsInline,
    errors: form.formState.errors,
    touched: form.formState.touchedFields,
    submitCount: form.formState.submitCount,
  });

  const isCompact = variant === "compact";

  return (
    <form
      className="w-full"
      noValidate
      onSubmit={
        onSubmit ? form.handleSubmit((values) => onSubmit(values)) : undefined
      }
      acceptCharset="ISO-8859-1"
    >
      <div
        className={clsx({
          "section-card shadow-md": inputsWrappedInCard,
        })}
      >
        <div
          className={clsx("flex flex-col gap-2", {
            "flex-row": variant === "compact",
          })}
        >
          {!isCompact && (
            <div className="relative flex flex-col gap-2">
              <HotelDestinationPicker
                analyticsSource={analyticsSource}
                form={form}
                showErrorsInline={showErrorsInline}
                onChange={onChange}
              />
            </div>
          )}

          <HotelDatePicker
            analyticsSource={analyticsSource}
            form={form}
            onChange={onChange}
            isCompact={isCompact}
          />

          <HotelVisitorsPicker
            analyticsSource={analyticsSource}
            form={form}
            onChange={onChange}
            isCompact={isCompact}
          />
        </div>
      </div>

      {submitButton ? submitButton : null}
    </form>
  );
}
